import PropTypes from 'prop-types';

/**
 * Read-only Renderer Hints block — surfaces x-uitype + decoded x-uioptions
 * without form inputs. Returns null when the field has no renderer hints.
 *
 * x-uioptions shapes (see RenderHintsForm for the canonical taxonomy):
 *   - dropdown:        JSON-encoded STRING of [{value, label}, ...]
 *   - radio:           native ARRAY of [{value, label}, ...]
 *   - multi-select:    native ARRAY of [{value, label}, ...]
 *   - file-upload:     native OBJECT {minItems, maxItems, allowedExtensions, allowedSizePerFileInMB}
 *   - dynamic-select:  JSON-encoded STRING with config keys (e.g. url, key)
 *   - textarea / '':   no options
 */

const NO_OPTIONS_UITYPES = new Set(['', 'textarea']);
const OPTION_LIST_UITYPES = new Set(['dropdown', 'radio', 'multi-select']);

function decodeOptionList(uitype, value) {
	if (value == null || value === '') return [];
	if (uitype === 'dropdown' && typeof value === 'string') {
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}
	return Array.isArray(value) ? value : [];
}

function decodeFileUpload(value) {
	if (value && typeof value === 'object' && !Array.isArray(value)) return value;
	return {};
}

function describeDynamicSelect(value) {
	if (value == null || value === '') return null;
	if (typeof value === 'string') {
		try {
			return JSON.parse(value);
		} catch {
			return null;
		}
	}
	if (typeof value === 'object') return value;
	return null;
}

function uitypeLabel(uitype) {
	if (!uitype) return null;
	return uitype.replace(/-/g, ' ');
}

export default function RenderHintsView({ field, fieldId }) {
	const xUitype = field.xUitype || '';
	const xUioptions = field.xUioptions;
	const hasUitype = !NO_OPTIONS_UITYPES.has(xUitype) || xUitype === 'textarea';

	if (!xUitype && (xUioptions == null || xUioptions === '')) return null;

	const rows = [];
	if (xUitype) rows.push(['Display as', uitypeLabel(xUitype)]);

	if (OPTION_LIST_UITYPES.has(xUitype)) {
		const list = decodeOptionList(xUitype, xUioptions);
		if (list.length > 0) {
			rows.push([
				`Options (${list.length})`,
				list.map((o) => o?.label ?? o?.value ?? '').filter(Boolean).join(', '),
			]);
		}
	} else if (xUitype === 'file-upload') {
		const opts = decodeFileUpload(xUioptions);
		if (opts.minItems != null) rows.push(['Min files', opts.minItems]);
		if (opts.maxItems != null) rows.push(['Max files', opts.maxItems]);
		if (Array.isArray(opts.allowedExtensions) && opts.allowedExtensions.length > 0) {
			rows.push(['Allowed extensions', opts.allowedExtensions.join(', ')]);
		}
		if (opts.allowedSizePerFileInMB != null) {
			rows.push(['Max size per file', `${opts.allowedSizePerFileInMB} MB`]);
		}
	} else if (xUitype === 'dynamic-select') {
		const cfg = describeDynamicSelect(xUioptions);
		if (cfg) {
			if (cfg.url) rows.push(['Source URL', cfg.url]);
			if (cfg.key) rows.push(['Lookup key', cfg.key]);
			if (cfg.value) rows.push(['Lookup value', cfg.value]);
			if (Array.isArray(cfg['fields-to-populate']) && cfg['fields-to-populate'].length > 0) {
				rows.push(['Auto-populates', cfg['fields-to-populate'].join(', ')]);
			}
		}
	}

	if (rows.length === 0 && !hasUitype) return null;
	if (rows.length === 0) return null;

	return (
		<section
			className="dsv-view__section dsv-view__section--renderer"
			aria-label="Renderer Hints"
			data-testid={`dsv-view-renderer-${fieldId}`}
			style={sectionStyle('#ea580c')}
		>
			<header style={sectionHeaderStyle}>Renderer</header>
			<dl style={dlStyle}>
				{rows.map(([label, value]) => (
					<div key={label} style={rowStyle}>
						<dt style={dtStyle}>{label}</dt>
						<dd style={ddStyle}>{String(value)}</dd>
					</div>
				))}
			</dl>
		</section>
	);
}

RenderHintsView.propTypes = {
	field: PropTypes.object.isRequired,
	fieldId: PropTypes.string,
};

function sectionStyle(accent) {
	return {
		borderLeft: `3px solid ${accent}`,
		background: '#f8fafc',
		padding: '10px 14px',
		margin: '8px 0',
		borderRadius: '0 4px 4px 0',
	};
}
const sectionHeaderStyle = {
	fontSize: '11px',
	fontWeight: 600,
	textTransform: 'uppercase',
	letterSpacing: '0.04em',
	color: '#374151',
	marginBottom: '6px',
};
const dlStyle = { display: 'grid', gridTemplateColumns: '160px 1fr', gap: '4px 14px', margin: 0 };
const rowStyle = { display: 'contents' };
const dtStyle = { color: '#6b7280', fontSize: '13px' };
const ddStyle = { margin: 0, fontSize: '13px', color: '#111827', wordBreak: 'break-word' };
